import React, { useState, useEffect, useCallback } from 'react'
import { toastError, toastSuccess, toastLoading, toastDismiss } from '../../utils/toastHelper'
import { useSearchParams } from 'react-router-dom'
import { usePagosAdmin, type Pago, type PagosFiltros, useEliminarPago, useEditarPago, useCrearPago } from '../../hooks/usePagosAdmin'
import { useReservationsAdmin, useReservaDetail, type Reserva } from '../../hooks/useReservationsAdmin'
import { useDeviceDetection } from '../../hooks/useDeviceDetection'
import { useTheme } from '../../hooks/useTheme'
import { useAuth } from '../../context/AuthContext'
import { useApiClient } from '../../hooks/useApiClient'
import { getStatusColors } from '../../utils/statusColorMap'
import { formatLocalDateToString, formatDateDisplay, formatDateTimeDisplay, parseLocalDateString } from '../../utils/dateHelpers'
import { ApiClient } from '../../services/api'
import ReservaDetailModal from '../../components/admin/ReservaDetailModal'
import EditarPagoModal from '../../components/admin/EditarPagoModal'
import ComprobanteViewer from '../../components/common/ComprobanteViewer'

const PaymentsAdmin: React.FC = () => {
  const { colors } = useTheme()
  const deviceInfo = useDeviceDetection()
  const { tienePermiso } = useAuth()
  const { apiClient } = useApiClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const [paginaActual, setPaginaActual] = useState(1)
  const ITEMS_POR_PAGINA = 15

  const [filtros, setFiltros] = useState<PagosFiltros>({
    limit: ITEMS_POR_PAGINA,
    offset: 0,
  })

  const [filtroEstado, setFiltroEstado] = useState<string>('todas')
  const [filtroProveedor, setFiltroProveedor] = useState<string>('todas')
  const [filtroTexto, setFiltroTexto] = useState<string>('')
  const [fechaDesde, setFechaDesde] = useState<string>('')
  const [fechaHasta, setFechaHasta] = useState<string>('')

  // Modal state - Detalle de pago
  const [selectedPago, setSelectedPago] = useState<Pago | undefined>()
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Modal state - Detalle de reserva (anidado)
  const [selectedReservaId, setSelectedReservaId] = useState<string | undefined>()
  const [isReservaModalOpen, setIsReservaModalOpen] = useState(false)

  // Modal state - Editar pago
  const [isEditarPagoModalOpen, setIsEditarPagoModalOpen] = useState(false)
  const [selectedPagoParaEditar, setSelectedPagoParaEditar] = useState<Pago | null>(null)

  // Hooks para editar/eliminar pagos
  const [editingPagoId, setEditingPagoId] = useState<number | null>(null)
  const [editingMontoValue, setEditingMontoValue] = useState<number>(0)
  const [editingMetodoValue, setEditingMetodoValue] = useState<string>('')

  // Modal para crear nuevo pago
  const [isCrearPagoModalOpen, setIsCrearPagoModalOpen] = useState(false)
  const [buscarReservaTexto, setBuscarReservaTexto] = useState<string>('')
  const [selectedReservaCrearPago, setSelectedReservaCrearPago] = useState<Reserva | null>(null)
  const [nuevoMonto, setNuevoMonto] = useState<number>(0)
  const [nuevoMetodo, setNuevoMetodo] = useState<string>('transferencia')
  const [nuevoReferencia, setNuevoReferencia] = useState<string>('')

  // ✅ Modal state - Delete confirmation
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{ isOpen: boolean; pago: Pago | null }>({ isOpen: false, pago: null })
  const [isDeleting, setIsDeleting] = useState(false)

  // Hook para buscar reservas
  const { data: reservasData, isLoading: loadingReservas } = useReservationsAdmin(
    buscarReservaTexto && isCrearPagoModalOpen
      ? { buscar: buscarReservaTexto, limit: 10, offset: 0 }
      : undefined
  )
  const reservasBusqueda = reservasData?.reservas || []

  // Hook para obtener detalle actualizado de la reserva seleccionada
  const { data: reservaDetailData } = useReservaDetail(selectedReservaCrearPago?.id)

  // Hook para crear pago (inicializado con reserva seleccionada)
  const crearPagoMutation = useCrearPago(selectedReservaCrearPago?.id || '')

  // Hook para eliminar pago
  const eliminarPagoMutation = useEliminarPago()

  // Procesar parámetros de URL al cargar la página
  useEffect(() => {
    const reservaParam = searchParams.get('reserva')
    if (reservaParam) {
      setFiltroTexto(reservaParam)
    }
  }, [searchParams])

  // Fetch pagos del backend
  const { data: responseData, isLoading, isError } = usePagosAdmin(filtros)
  const pagos = responseData?.pagos || []
  const totalPagos = responseData?.total || 0

  // State para estadísticas
  const [stats, setStats] = useState({
    total_pagos: 0,
    pagos_aprobados: 0,
    pagos_pendientes: 0,
    pagos_rechazados: 0,
    total_monto_aprobado: 0,
    total_monto_pendiente: 0,
    total_monto_rechazado: 0,
    total_monto_general: 0,
  })
  const [statsLoading, setStatsLoading] = useState(false)

  // Resetear a página 1 cuando cambian los filtros
  useEffect(() => {
    setPaginaActual(1)
  }, [filtroEstado, filtroProveedor, filtroTexto, fechaDesde, fechaHasta])

  // Actualizar filtros cuando cambian
  useEffect(() => {
    const nuevosFiltros: PagosFiltros = {
      limit: ITEMS_POR_PAGINA,
      offset: (paginaActual - 1) * ITEMS_POR_PAGINA,
    }

    if (filtroEstado !== 'todas') {
      nuevosFiltros.estado = filtroEstado
    }

    if (filtroProveedor !== 'todas') {
      nuevosFiltros.proveedor_pago = filtroProveedor
    }

    if (filtroTexto) {
      nuevosFiltros.buscar = filtroTexto
    }

    if (fechaDesde) {
      nuevosFiltros.fecha_desde = fechaDesde
    }

    if (fechaHasta) {
      nuevosFiltros.fecha_hasta = fechaHasta
    }

    setFiltros(nuevosFiltros)
  }, [filtroEstado, filtroProveedor, filtroTexto, fechaDesde, fechaHasta, paginaActual])

  // Función para obtener estadísticas del backend
  const obtenerStats = useCallback(async () => {
    setStatsLoading(true)
    try {
      const params = new URLSearchParams()

      if (filtroEstado !== 'todas') {
        params.append('estado', filtroEstado)
      }

      if (fechaDesde) {
        params.append('fecha_desde', fechaDesde)
      }

      if (fechaHasta) {
        params.append('fecha_hasta', fechaHasta)
      }

      const data = await ApiClient.get<typeof stats>(
        `/api/payments/stats/resumen?${params.toString()}`
      )

      setStats(data)
    } catch (error) {
      console.error('Error obteniendo estadísticas:', error)
    } finally {
      setStatsLoading(false)
    }
  }, [filtroEstado, fechaDesde, fechaHasta])

  // Cargar estadísticas cuando cambian los filtros
  useEffect(() => {
    obtenerStats()
  }, [obtenerStats])

  const getEstadoPagoColor = (estado: string) => {
    let statusType: 'confirmed' | 'pending' | 'cancelled' = 'pending'

    // Mapear estados de pagos al tipo de estado para colores
    switch (estado?.toLowerCase()) {
      case 'aprobado':
        statusType = 'confirmed'
        break
      case 'rechazado':
        statusType = 'cancelled'
        break
      case 'pendiente':
        statusType = 'pending'
        break
      default:
        statusType = 'pending'
    }

    const statusColors = getStatusColors(statusType, colors)
    return {
      backgroundColor: statusColors.backgroundColor,
      color: statusColors.textColor,
      borderColor: statusColors.borderColor
    }
  }

  const limpiarFiltros = () => {
    setFiltroEstado('todas')
    setFiltroProveedor('todas')
    setFiltroTexto('')
    setFechaDesde('')
    setFechaHasta('')
  }

  const handleOpenModal = (pago: Pago) => {
    setSelectedPago(pago)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedPago(undefined)
  }

  const handleOpenReservaModal = (reservaId: string) => {
    setSelectedReservaId(reservaId)
    setIsReservaModalOpen(true)
  }

  const handleCloseReservaModal = () => {
    setIsReservaModalOpen(false)
    setSelectedReservaId(undefined)
  }

  const handleStartEdit = (pago: Pago) => {
    if (!tienePermiso('pagos.editar')) {
      toastError('No tienes permisos para editar pagos')
      return
    }
    setEditingPagoId(pago.id)
    setEditingMontoValue(pago.monto)
    setEditingMetodoValue(pago.proveedor_pago || '')
  }

  const handleSaveEdit = async (pago: Pago) => {
    if (!tienePermiso('pagos.editar')) {
      toastError('No tienes permisos para editar pagos')
      return
    }

    if (editingMontoValue <= 0) {
      toastError('El monto debe ser mayor a 0')
      return
    }

    const editarMutation = useEditarPago(pago.reserva_id)
    try {
      await editarMutation.mutateAsync({
        pagoId: pago.id,
        datos: {
          monto: editingMontoValue,
          metodo_pago: editingMetodoValue || undefined
        }
      })
      setEditingPagoId(null)
    } catch (error) {
      console.error('Error al editar pago:', error)
    }
  }

  const handleDelete = (pago: Pago) => {
    if (!tienePermiso('pagos.eliminar')) {
      toastError('No tienes permisos para eliminar pagos')
      return
    }
    setDeleteConfirmModal({ isOpen: true, pago })
  }

  const handleConfirmDelete = async () => {
    if (!deleteConfirmModal.pago) return

    setIsDeleting(true)
    try {
      await eliminarPagoMutation.mutateAsync({
        pagoId: deleteConfirmModal.pago.id,
        reservaId: deleteConfirmModal.pago.reserva_id
      })
      setDeleteConfirmModal({ isOpen: false, pago: null })
      // Refrescar estadísticas después de eliminar el pago
      await obtenerStats()
    } catch (error) {
      console.error('Error al eliminar pago:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const getSaldoPendiente = (reserva: Reserva): number => {
    const monto_pagado = reserva.monto_pagado || 0
    return Math.max(0, reserva.monto_total - monto_pagado)
  }

  // Usar datos más recientes de la reserva si están disponibles
  const reservaActual = reservaDetailData || selectedReservaCrearPago

  const handleCrearPago = async () => {
    if (!tienePermiso('pagos.crear')) {
      toastError('No tienes permisos para crear pagos')
      return
    }

    if (!reservaActual) {
      toastError('Selecciona una reserva')
      return
    }

    const saldoPendiente = getSaldoPendiente(reservaActual)
    if (saldoPendiente <= 0) {
      toastError('Esta reserva no tiene saldo pendiente')
      return
    }

    if (nuevoMonto <= 0) {
      toastError('El monto debe ser mayor a 0')
      return
    }

    if (nuevoMonto > saldoPendiente) {
      toastError(`El monto no puede superar el saldo pendiente de $${saldoPendiente.toLocaleString('es-CL')}`)
      return
    }

    if (!nuevoMetodo.trim()) {
      toastError('Selecciona un método de pago')
      return
    }

    try {
      await crearPagoMutation.mutateAsync({
        monto: nuevoMonto,
        metodo_pago: nuevoMetodo,
        referencia_pago: nuevoReferencia || undefined
      })

      // Resetear formulario
      setBuscarReservaTexto('')
      setSelectedReservaCrearPago(null)
      setNuevoMonto(0)
      setNuevoMetodo('transferencia')
      setNuevoReferencia('')
      setIsCrearPagoModalOpen(false)
    } catch (error) {
      console.error('Error al crear pago:', error)
    }
  }

  const handleResetCrearPagoModal = () => {
    setIsCrearPagoModalOpen(false)
    setBuscarReservaTexto('')
    setSelectedReservaCrearPago(null)
    setNuevoMonto(0)
    setNuevoMetodo('transferencia')
    setNuevoReferencia('')
  }

  // Verificar permiso
  if (!tienePermiso('pagos.ver')) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-700 font-semibold">❌ Acceso Denegado</p>
            <p className="text-red-600 mt-2">No tienes permisos para acceder a la gestión de pagos.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Gestión de Pagos
            </h1>
            <p className="text-gray-600">
              Visualiza y gestiona todos los pagos de reservas manuales
            </p>
          </div>
          {tienePermiso('pagos.crear') && (
            <button
              onClick={() => setIsCrearPagoModalOpen(true)}
              className="px-4 py-2 font-medium text-white rounded-lg transition-colors"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.primaryDark}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.primary}
            >
              ➕ Agregar Pago
            </button>
          )}
        </div>

        {isLoading && (
          <div className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
            <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full"></div>
            <span>Cargando pagos...</span>
          </div>
        )}
        {isError && (
          <div className="flex items-center space-x-2 text-sm mb-6" style={{ color: colors.primaryDark }}>
            <span>❌ Error al cargar pagos</span>
          </div>
        )}

        {/* Stats */}
        <div className={`grid gap-6 mb-8 ${
          deviceInfo.isMobile
            ? 'grid-cols-1'
            : deviceInfo.isTablet
            ? 'grid-cols-2'
            : 'grid-cols-4'
        }`}>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Pagos</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total_pagos}</p>
              </div>
              <div className="text-4xl">💳</div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Aprobados</p>
                <p className="text-3xl font-bold" style={{ color: colors.primary }}>
                  {stats.pagos_aprobados}
                </p>
              </div>
              <div className="text-4xl">✓</div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Pendientes</p>
                <p className="text-3xl font-bold text-orange-600">{stats.pagos_pendientes}</p>
              </div>
              <div className="text-4xl">⏳</div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Monto Aprobado</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${stats.total_monto_aprobado.toLocaleString('es-CL')}
                </p>
              </div>
              <div className="text-4xl">💰</div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtros</h2>
          <div className={`grid gap-4 ${
            deviceInfo.isMobile ? 'grid-cols-1' : 'grid-cols-4'
          }`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estado
              </label>
              <select
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0"
                style={{ focusOutlineColor: colors.primary }}
              >
                <option value="todas">Todos</option>
                <option value="aprobado">Aprobado</option>
                <option value="pendiente">Pendiente</option>
                <option value="rechazado">Rechazado</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Proveedor
              </label>
              <select
                value={filtroProveedor}
                onChange={(e) => setFiltroProveedor(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0"
                style={{ focusOutlineColor: colors.primary }}
              >
                <option value="todas">Todos</option>
                <option value="manual">Manual</option>
                <option value="getnet">Getnet</option>
                <option value="khipu">Khipu</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Desde
              </label>
              <input
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0"
                style={{ focusOutlineColor: colors.primary }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hasta
              </label>
              <input
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0"
                style={{ focusOutlineColor: colors.primary }}
              />
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <input
              type="text"
              placeholder="Buscar por ID de reserva..."
              value={filtroTexto}
              onChange={(e) => setFiltroTexto(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0"
              style={{ focusOutlineColor: colors.primary }}
            />
            {(filtroEstado !== 'todas' || filtroProveedor !== 'todas' || filtroTexto || fechaDesde || fechaHasta) && (
              <button
                onClick={limpiarFiltros}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Limpiar Filtros
              </button>
            )}
          </div>
        </div>

        {/* Lista de Pagos */}
        {pagos.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
            <p className="text-gray-500 mb-2">📭 No hay pagos para mostrar</p>
            <p className="text-sm text-gray-400">
              Intenta con otros filtros o crea una reserva manual
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Vista Desktop - Tabla */}
            {!deviceInfo.isMobile && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID Pago
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reserva
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Monto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Proveedor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pagos.map((pago) => (
                    <tr key={pago.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{pago.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="relative group inline-block">
                          <span className="cursor-help border-b border-dotted border-gray-400">
                            {pago.reserva_id}
                          </span>
                          {/* Tooltip en hover */}
                          {(pago.cliente_nombre || pago.espacios_nombres) && (
                            <div className="invisible group-hover:visible absolute z-10 left-0 top-full mt-1 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg pointer-events-none">
                              {pago.cliente_nombre && (
                                <div className="mb-1">
                                  <span className="font-semibold">Cliente:</span> {pago.cliente_nombre}
                                </div>
                              )}
                              {pago.espacios_nombres && (
                                <div>
                                  <span className="font-semibold">Espacio(s):</span> {pago.espacios_nombres}
                                </div>
                              )}
                              {/* Flecha del tooltip */}
                              <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        ${pago.monto.toLocaleString('es-CL')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className="capitalize">{pago.proveedor_pago}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                          style={getEstadoPagoColor(pago.estado)}
                        >
                          {pago.estado?.toLowerCase() === 'aprobado' ? '✓' : pago.estado?.toLowerCase() === 'pendiente' ? '⏳' : '✗'} {pago.estado?.charAt(0).toUpperCase()}{pago.estado?.slice(1).toLowerCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {pago.fecha_pago ? (
                          parseLocalDateString(pago.fecha_pago).toLocaleDateString('es-CL')
                        ) : (
                          <span className="text-gray-400">Sin fecha</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          {tienePermiso('pagos.editar') && (
                            <button
                              onClick={() => {
                                setSelectedPagoParaEditar(pago)
                                setIsEditarPagoModalOpen(true)
                              }}
                              className="text-gray-400 hover:text-yellow-600 transition-colors"
                              title="Editar pago y comprobante"
                            >
                              ✏️
                            </button>
                          )}
                          <button
                            onClick={() => handleOpenModal(pago)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            title="Ver detalles del pago"
                          >
                            🔍
                          </button>
                          {tienePermiso('pagos.eliminar') && (
                            <button
                              onClick={() => handleDelete(pago)}
                              className="text-gray-400 hover:text-red-600 transition-colors"
                              title="Eliminar pago"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}

            {/* Vista Mobile - Cards */}
            {deviceInfo.isMobile && (
              <div className="divide-y divide-gray-200">
                {pagos.map((pago) => (
                  <div key={pago.id} className="p-4 hover:bg-gray-50 transition-colors">
                    {/* Header con ID y acciones */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-xs text-gray-500 uppercase mb-1">ID Pago</p>
                        <p className="text-sm font-bold text-gray-900">#{pago.id}</p>
                      </div>
                      <div className="flex gap-2">
                        {tienePermiso('pagos.editar') && (
                          <button
                            onClick={() => {
                              setSelectedPagoParaEditar(pago)
                              setIsEditarPagoModalOpen(true)
                            }}
                            className="text-gray-400 hover:text-yellow-600 transition-colors p-1"
                            title="Editar pago"
                          >
                            ✏️
                          </button>
                        )}
                        <button
                          onClick={() => handleOpenModal(pago)}
                          className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                          title="Ver detalles"
                        >
                          🔍
                        </button>
                        {tienePermiso('pagos.eliminar') && (
                          <button
                            onClick={() => handleDelete(pago)}
                            className="text-gray-400 hover:text-red-600 transition-colors p-1"
                            title="Eliminar pago"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Monto destacado */}
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 uppercase mb-1">Monto</p>
                      <p className="text-lg font-bold text-gray-900">
                        ${pago.monto.toLocaleString('es-CL')}
                      </p>
                    </div>

                    {/* Información de reserva */}
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 uppercase mb-1">Reserva</p>
                      <p className="text-sm font-medium text-gray-900 break-words">{pago.reserva_id}</p>
                      {pago.cliente_nombre && (
                        <p className="text-xs text-gray-500 mt-1 break-words">{pago.cliente_nombre}</p>
                      )}
                      {pago.espacios_nombres && (
                        <p className="text-xs text-gray-400 mt-0.5 break-words">{pago.espacios_nombres}</p>
                      )}
                    </div>

                    {/* Detalles adicionales */}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <p className="text-xs text-gray-500 uppercase mb-1">Proveedor</p>
                        <p className="text-sm text-gray-700 capitalize">{pago.proveedor_pago}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase mb-1">Fecha Pago</p>
                        <p className="text-sm text-gray-700">
                          {pago.fecha_pago ? (
                            parseLocalDateString(pago.fecha_pago).toLocaleDateString('es-CL')
                          ) : (
                            <span className="text-gray-400">Sin fecha</span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Badge de estado */}
                    <div>
                      <span
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                        style={getEstadoPagoColor(pago.estado)}
                      >
                        {pago.estado?.toLowerCase() === 'aprobado' ? '✓' : pago.estado?.toLowerCase() === 'pendiente' ? '⏳' : '✗'}{' '}
                        {pago.estado?.charAt(0).toUpperCase()}{pago.estado?.slice(1).toLowerCase()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Paginación */}
            {pagos.length > 0 && (
              <div className={`mt-6 border-t border-gray-200 pt-4 ${
                deviceInfo.isMobile ? 'space-y-3' : 'flex items-center justify-between'
              }`}>
                {/* Contador de resultados */}
                <div className={`text-sm text-gray-600 ${deviceInfo.isMobile ? 'text-center' : ''}`}>
                  Mostrando {(paginaActual - 1) * ITEMS_POR_PAGINA + 1} a{' '}
                  {Math.min(paginaActual * ITEMS_POR_PAGINA, totalPagos)} de {totalPagos} pagos
                </div>

                {/* Botones de paginación */}
                <div className={`flex gap-2 ${deviceInfo.isMobile ? 'justify-center' : ''}`}>
                  {/* Botón Anterior */}
                  <button
                    onClick={() => setPaginaActual(Math.max(1, paginaActual - 1))}
                    disabled={paginaActual === 1}
                    className={`${
                      deviceInfo.isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'
                    } font-medium rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors`}
                    title="Página anterior"
                  >
                    {deviceInfo.isMobile ? '←' : '← Anterior'}
                  </button>

                  {/* Números de página */}
                  <div className="flex items-center gap-1">
                    {Array.from({
                      length: Math.ceil(totalPagos / ITEMS_POR_PAGINA),
                    }).map((_, index) => {
                      const pagina = index + 1
                      const esPaginaActual = pagina === paginaActual
                      const totalPaginas = Math.ceil(totalPagos / ITEMS_POR_PAGINA)

                      // ✅ Control inteligente de páginas para mobile Y desktop
                      const maxPaginasVisibles = deviceInfo.isMobile ? 3 : 7 // Mobile: 3, Desktop: 7
                      const rango = Math.floor(maxPaginasVisibles / 2)

                      // Siempre mostrar primera y última
                      const mostrarPrimera = pagina === 1
                      const mostrarUltima = pagina === totalPaginas

                      // Mostrar páginas alrededor de la actual
                      const mostrarAlrededor = Math.abs(pagina - paginaActual) <= rango

                      const showPage = mostrarPrimera || mostrarUltima || mostrarAlrededor

                      if (!showPage) {
                        // Mostrar "..." solo una vez entre grupos
                        if (pagina === paginaActual - rango - 1 || pagina === paginaActual + rango + 1) {
                          return <span key={pagina} className="px-2 text-gray-400">...</span>
                        }
                        return null
                      }

                      return (
                        <button
                          key={pagina}
                          onClick={() => setPaginaActual(pagina)}
                          className={`${
                            deviceInfo.isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'
                          } font-medium rounded-lg transition-colors ${
                            esPaginaActual
                              ? 'bg-gray-900 text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                          title={`Ir a página ${pagina}`}
                        >
                          {pagina}
                        </button>
                      )
                    })}
                  </div>

                  {/* Botón Siguiente */}
                  <button
                    onClick={() => {
                      const totalPaginas = Math.ceil(totalPagos / ITEMS_POR_PAGINA)
                      setPaginaActual(Math.min(totalPaginas, paginaActual + 1))
                    }}
                    disabled={paginaActual >= Math.ceil(totalPagos / ITEMS_POR_PAGINA)}
                    className={`${
                      deviceInfo.isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'
                    } font-medium rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors`}
                    title="Página siguiente"
                  >
                    {deviceInfo.isMobile ? '→' : 'Siguiente →'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Modal de Detalles */}
        {isModalOpen && selectedPago && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Detalles del Pago #{selectedPago.id}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  ✕
                </button>
              </div>

              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">ID Pago</p>
                    <p className="font-semibold text-gray-900">#{selectedPago.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">ID Reserva</p>
                    <button
                      onClick={() => handleOpenReservaModal(selectedPago.reserva_id)}
                      className="font-semibold transition-colors"
                      style={{ color: colors.primary }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      title="Click para ver detalles de la reserva"
                    >
                      {selectedPago.reserva_id} 🔍
                    </button>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Monto</p>
                    <p className="font-semibold text-gray-900">
                      ${selectedPago.monto.toLocaleString('es-CL')} {selectedPago.moneda}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Proveedor</p>
                    <p className="font-semibold text-gray-900 capitalize">{selectedPago.proveedor_pago}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Estado</p>
                    <span
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                      style={getEstadoPagoColor(selectedPago.estado)}
                    >
                      {selectedPago.estado.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Fecha de Pago</p>
                    <p className="font-semibold text-gray-900">
                      {selectedPago.fecha_pago ? formatDateDisplay(selectedPago.fecha_pago) : 'Sin fecha'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Fecha de Creación</p>
                    <p className="font-semibold text-gray-900">
                      {formatDateTimeDisplay(selectedPago.fecha_creacion)}
                    </p>
                  </div>
                </div>

                {/* 📎 Sección de Comprobante de Pago (Solo Lectura) */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    📎 Comprobante de Pago
                  </h3>

                  {selectedPago.comprobante_path ? (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <ComprobanteViewer
                        pagoId={selectedPago.id}
                        filename={selectedPago.comprobante_filename}
                        mimeType={selectedPago.comprobante_mime_type}
                        size={selectedPago.comprobante_size}
                        uploadDate={selectedPago.comprobante_upload_date}
                        showMetadata={true}
                      />
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
                      <p className="text-sm text-gray-600">
                        📭 No hay comprobante cargado
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Para cargar o editar el comprobante, usa la opción de Editar Pago
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex gap-2 justify-end">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Reserva (anidado dentro del modal de pago) */}
        <ReservaDetailModal
          reservaId={selectedReservaId}
          isOpen={isReservaModalOpen}
          onClose={handleCloseReservaModal}
        />

        {/* Modal para crear nuevo pago */}
        {isCrearPagoModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
                <h2 className="text-lg font-semibold text-gray-900">
                  Crear Nuevo Pago
                </h2>
                <button
                  onClick={handleResetCrearPagoModal}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  ✕
                </button>
              </div>

              <div className="px-6 py-4 space-y-4">
                {/* Búsqueda de Reserva */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {selectedReservaCrearPago ? '✓ Reserva Seleccionada' : 'Buscar Reserva *'}
                  </label>
                  {!selectedReservaCrearPago ? (
                    <div>
                      <input
                        type="text"
                        value={buscarReservaTexto}
                        onChange={(e) => setBuscarReservaTexto(e.target.value)}
                        placeholder="Ingresa ID, nombre del cliente o número de reserva..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0"
                        style={{ focusOutlineColor: colors.primary }}
                      />
                      {buscarReservaTexto && (
                        <div className="mt-2 border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                          {loadingReservas ? (
                            <div className="p-4 text-center text-sm text-gray-500">
                              Buscando reservas...
                            </div>
                          ) : reservasBusqueda.length === 0 ? (
                            <div className="p-4 text-center text-sm text-gray-500">
                              No se encontraron reservas
                            </div>
                          ) : (
                            <div className="divide-y">
                              {reservasBusqueda.map((reserva) => {
                                const saldoPendiente = getSaldoPendiente(reserva)
                                const tieneSaldo = saldoPendiente > 0
                                return (
                                  <button
                                    key={reserva.id}
                                    onClick={() => {
                                      setSelectedReservaCrearPago(reserva)
                                      setBuscarReservaTexto('')
                                    }}
                                    disabled={!tieneSaldo}
                                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${!tieneSaldo ? 'bg-gray-50' : ''}`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex-1">
                                        <p className="font-medium text-gray-900">{reserva.cliente_nombre || reserva.huesped_nombre}</p>
                                        <p className="text-xs text-gray-500">ID: {reserva.id}</p>
                                        <p className="text-xs text-gray-600">
                                          {reserva.fecha_entrada} a {reserva.fecha_salida}
                                        </p>
                                      </div>
                                      <div className="text-right">
                                        <p className={`text-sm font-semibold ${tieneSaldo ? 'text-orange-600' : 'text-gray-400'}`}>
                                          ${saldoPendiente.toLocaleString('es-CL')} pendiente
                                        </p>
                                        {!tieneSaldo && (
                                          <p className="text-xs text-gray-400">Pagada</p>
                                        )}
                                      </div>
                                    </div>
                                  </button>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 rounded-lg" style={{ backgroundColor: `${colors.primary}15` }}>
                      <p className="font-medium text-gray-900">{reservaActual?.cliente_nombre || reservaActual?.huesped_nombre}</p>
                      <p className="text-sm text-gray-600">ID: {reservaActual?.id}</p>
                      <p className="text-sm text-gray-600">
                        Total: ${reservaActual?.monto_total.toLocaleString('es-CL')} |
                        Pagado: ${(reservaActual?.monto_pagado || 0).toLocaleString('es-CL')} |
                        Pendiente: ${reservaActual ? getSaldoPendiente(reservaActual).toLocaleString('es-CL') : '0'}
                      </p>
                      <button
                        onClick={() => {
                          setSelectedReservaCrearPago(null)
                          setBuscarReservaTexto('')
                        }}
                        className="mt-2 text-xs px-2 py-1 text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50"
                      >
                        Cambiar reserva
                      </button>
                    </div>
                  )}
                </div>

                {reservaActual && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Monto *
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          value={nuevoMonto}
                          onChange={(e) => setNuevoMonto(parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0"
                          style={{ focusOutlineColor: colors.primary }}
                          min="0"
                          step="100"
                          max={getSaldoPendiente(reservaActual)}
                        />
                        <span className="absolute right-3 top-2.5 text-xs text-gray-500">
                          Máx: ${getSaldoPendiente(reservaActual).toLocaleString('es-CL')}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Método de Pago *
                      </label>
                      <select
                        value={nuevoMetodo}
                        onChange={(e) => setNuevoMetodo(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0"
                        style={{ focusOutlineColor: colors.primary }}
                      >
                        <option value="transferencia">Transferencia</option>
                        <option value="efectivo">Efectivo</option>
                        <option value="cheque">Cheque</option>
                        <option value="tarjeta">Tarjeta</option>
                        <option value="otro">Otro</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Referencia de Pago (opcional)
                      </label>
                      <input
                        type="text"
                        value={nuevoReferencia}
                        onChange={(e) => setNuevoReferencia(e.target.value)}
                        placeholder="Ej: TRF-001234, Comprobante, Número de cheque"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0"
                        style={{ focusOutlineColor: colors.primary }}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex gap-2 justify-end sticky bottom-0 bg-white">
                <button
                  onClick={handleResetCrearPagoModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCrearPago}
                  disabled={!reservaActual}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: reservaActual ? colors.primary : '#ccc' }}
                  onMouseEnter={(e) => reservaActual && (e.currentTarget.style.backgroundColor = colors.primaryDark)}
                  onMouseLeave={(e) => reservaActual && (e.currentTarget.style.backgroundColor = colors.primary)}
                >
                  ✓ Crear Pago
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Edición de Pago (NUEVO) */}
        <EditarPagoModal
          isOpen={isEditarPagoModalOpen}
          pago={selectedPagoParaEditar}
          onClose={() => {
            setIsEditarPagoModalOpen(false)
            setSelectedPagoParaEditar(null)
          }}
          onSuccess={() => {
            // Refrescar la lista de pagos
            // Si hay un hook de refetch en usePagosAdmin, usarlo aquí
          }}
        />

        {/* ✅ Delete Confirmation Modal */}
        {deleteConfirmModal.isOpen && deleteConfirmModal.pago && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="bg-white rounded-lg shadow-xl max-w-sm w-full"
              style={{ borderTop: `4px solid #ef4444` }}
            >
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">🗑️ Eliminar Pago</h3>
                <p className="text-gray-600 mb-6">
                  ¿Estás seguro de que deseas eliminar este pago? Los estados se recalcularán automáticamente.
                </p>
                <div className="bg-gray-50 p-3 rounded-lg mb-6">
                  <p className="text-sm text-gray-700">
                    <strong>Monto:</strong> ${deleteConfirmModal.pago.monto.toLocaleString('es-CL')}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>Método:</strong> {deleteConfirmModal.pago.metodo_pago || 'No especificado'}
                  </p>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setDeleteConfirmModal({ isOpen: false, pago: null })}
                    disabled={isDeleting}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    disabled={isDeleting}
                    className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
                    style={{ backgroundColor: '#ef4444' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = isDeleting ? '0.5' : '0.9'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '1'
                    }}
                  >
                    {isDeleting ? '⏳ Eliminando...' : '🗑️ Eliminar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PaymentsAdmin
